import React from "react";
import { motion } from "framer-motion";
import { Box, Typography, Button } from "@mui/material";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";

export type NoProductItemsFoundProps = {
  onAddItem: () => void;
};

const NoProductItemsFound: React.FC<NoProductItemsFoundProps> = ({
  onAddItem
}) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      sx={{
        p: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        minHeight: "50vh"
      }}
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        sx={{
          width: 72,
          height: 72,
          borderRadius: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "action.hover",
          mb: 2
        }}
      >
        <LayersOutlinedIcon sx={{ fontSize: 36, color: "text.disabled" }} />
      </Box>
      <Typography
        component={motion.div}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        variant="h5"
        fontWeight="700"
        mb={1}
      >
        {t("no-product-items-found-title", "No Product Items Found")}
      </Typography>
      <Typography
        component={motion.div}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        color="text.secondary"
        mb={3}
      >
        {t(
          "no-product-items-found-description-line1",
          "It looks like there are no items for this product yet."
        )}
        <br />
        {t(
          "no-product-items-found-description-line2",
          "Each label gets a unique QR code for faster checkout."
        )}
        <br />
        {t(
          "no-product-items-found-example",
          "Example: 10 oil drums → 10 labels, 14 liters in each."
        )}
      </Typography>

      <Button
        component={motion.button}
        variant="contained"
        size="large"
        onClick={onAddItem}
        data-testid="product-item-list-add-btn"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        sx={{
          backgroundColor: BRAND_COLOR,
          textTransform: "none",
          fontWeight: 600,
          px: 3,
          py: 1.5,
          boxShadow: `0 2px 12px ${BRAND_COLOR}40`,
          "&:hover": {
            backgroundColor: BRAND_HOVER,
            boxShadow: `0 4px 16px ${BRAND_COLOR}50`
          }
        }}
      >
        {t("no-product-items-found-add-item-button", "Add new item(s)")}
      </Button>
    </Box>
  );
};

export default NoProductItemsFound;
